import type { ReactNode } from 'react';
import { useRequestDetail } from './use-request-detail';
import { RequestSurface, type RequestMode } from './request-surface';

/**
 * A request opened OVER a list — the drawer and the modal.
 *
 * ⚠️ **It owns the fetch, not the caller.** A queue holds rows; the detail needs the day breakdown,
 * the balance, the thread and the attachments, which only the detail endpoints assemble. Passing a
 * row in and hoping would render a detail missing most of itself — `task-overlay.tsx`, verbatim.
 *
 * ⚠️ **`full` is not handled here** — it is an address (R-8), so the caller navigates to
 * `/requests/:identifier` instead. Rendering a page inside an overlay would put the same request on screen
 * twice, one over the other.
 */
export function RequestOverlay({
  requestId,
  mode,
  onModeChange,
  onClose,
  onChanged,
  onOpenFull,
  onOpenTask,
}: {
  /** `null` closes it. */
  readonly requestId: string | null;
  readonly mode: RequestMode;
  readonly onModeChange: (next: RequestMode) => void;
  readonly onClose: () => void;
  /** So the list underneath can re-read a row this overlay just changed. */
  readonly onChanged: () => void;
  /** Receives the request's public identifier — the page's address names it by that (R-57). */
  readonly onOpenFull: (identifier: string) => void;
  readonly onOpenTask: (key: string) => void;
}): ReactNode {
  /* ⚠️ The hook runs unconditionally with a null id — see `use-request-detail.ts`. */
  const { data, serverError, handlers } = useRequestDetail(requestId);

  if (requestId === null || data === null) return null;

  return (
    <RequestSurface
      mode={mode === 'full' ? 'modal' : mode}
      open
      data={data}
      serverError={serverError}
      handlers={{
        ...handlers,
        /* ⚠️ The third argument carries HOW an approved claim settles and, since `0147`, what
           it is priced as — dropping it here silently un-chose both. */
        onDecide: (decision, note, settle) => {
          handlers.onDecide(decision, note, settle);
          onChanged();
        },
        onRevoke: () => {
          handlers.onRevoke();
          onChanged();
        },
        onOpenTask,
      }}
      onModeChange={(next) => {
        /* ⚠️ Growing to `full` LEAVES the overlay: the page is an address, so this navigates and
           the drawer closes behind it. */
        if (next === 'full') {
          onOpenFull(data.kind === 'leave' ? data.request.identifier : data.claim.identifier);
          onClose();
          return;
        }
        onModeChange(next);
      }}
      onClose={onClose}
    />
  );
}
